import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import { 
  FileText, Save, ArrowLeft, User, Building, Pill, Heart, 
  Activity, CheckCircle, ClipboardList, Download, Printer
} from 'lucide-react';
import { generateDischargePDF, downloadPDF, printPDF } from '../utils/pdfExport';
import { cachePdfPatients } from '../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DischargeAssessmentForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    dischargeDate: new Date().toISOString().split('T')[0],
    disciplineCompleting: 'RN',
    dischargeDisposition: 1,
    // Emergent Care
    usedEmergencyDept: null,
    emergentCareReason: [],
    inpatientFacility: null,
    // Medication List Transfer
    medListToProvider: null,
    medListToPatient: null,
    medListTransmissionRoute: [],
    // Summary
    primaryCaregiverName: '',
    primaryCaregiverPhone: '',
    dischargePlanReviewed: [],
    // Cognitive/Mood
    bimsTotal: null,
    cognitiveFunction: null,
    phqTotal: null,
    // Functional Status
    selfCareScore: null,
    mobilityScore: null,
    feedingScore: null,
    // Skin Conditions
    hasPressureUlcer: null,
    hasStasisUlcer: null,
    hasSurgicalWound: null,
    // Interventions
    fallPreventionIncluded: null,
    depressionIntervention: null,
    painIntervention: null,
    pressureUlcerPrevention: null,
    // Discharge Summary
    dischargeSummary: '',
    wasPlannedDischarge: true,
    reasonForDischarge: '',
    // Signatures
    patientSignature: '',
    clinicianSignature: ''
  });

  useEffect(() => {
    fetchPatients();
    if (formId) {
      fetchForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients); cachePdfPatients(data.patients);
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) {
            setFormData(prev => ({
              ...prev,
              patientName: `${patient.firstName} ${patient.lastName}`
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/discharge/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setFormData(data.assessment);
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientChange = (e) => {
    const selectedPatientId = e.target.value;
    const patient = patients.find(p => p.id === selectedPatientId);
    if (patient) {
      setFormData(prev => ({
        ...prev,
        patientId: selectedPatientId,
        patientName: `${patient.firstName} ${patient.lastName}`
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field] || []), value]
        : (prev[field] || []).filter(v => v !== value)
    }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId 
        ? `${API_URL}/api/forms/discharge/${formId}`
        : `${API_URL}/api/forms/discharge`;
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Assessment updated' : 'Assessment created');
        if (!formId && data.assessment) {
          navigate(`/dashboard/forms/discharge?formId=${data.assessment.id}`);
        }
      } else {
        toast.error('Failed to save assessment');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!formId) {
      toast.error('Please save the assessment first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/discharge/${formId}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Assessment completed successfully');
        navigate('/dashboard/clinical/oasis');
      }
    } catch (error) {
      toast.error('Failed to complete assessment');
    }
  };

  const handleExportPDF = () => {
    const doc = generateDischargePDF(formData);
    downloadPDF(doc, `discharge-${formData.patientName || 'assessment'}.pdf`);
  };

  const handlePrintPDF = () => {
    const doc = generateDischargePDF(formData);
    printPDF(doc);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard/clinical/oasis')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#f39c12]" />
                  Discharge Assessment
                </h1>
                <p className="text-gray-500 text-sm mt-1">Including OASIS Elements for Discharge</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Button 
                variant="outline"
                onClick={handlePrintPDF}
                data-testid="print-pdf-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                variant="outline"
              >
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save</>}
              </Button>
              {formId && (
                <Button 
                  onClick={handleComplete}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Complete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Section A: Administrative */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Section A: Administrative Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Patient *</Label>
              <select
                value={formData.patientId}
                onChange={handlePatientChange}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
              >
                <option value="">Select a patient</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Discharge Date *</Label>
              <Input
                type="date"
                value={formData.dischargeDate}
                onChange={(e) => handleInputChange('dischargeDate', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>M0080. Discipline Completing Assessment</Label>
              <select
                value={formData.disciplineCompleting}
                onChange={(e) => handleInputChange('disciplineCompleting', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
              >
                <option value="RN">1 - RN</option>
                <option value="PT">2 - PT</option>
                <option value="SLP/ST">3 - SLP/ST</option>
                <option value="OT">4 - OT</option>
              </select>
            </div>
            <div>
              <Label>M2420. Discharge Disposition</Label>
              <select
                value={formData.dischargeDisposition}
                onChange={(e) => handleInputChange('dischargeDisposition', parseInt(e.target.value))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
              >
                <option value={1}>1 - Remained in community (without formal services)</option>
                <option value={2}>2 - Remained in community (with formal services)</option>
                <option value={3}>3 - Transferred to non-institutional hospice</option>
                <option value={4}>4 - Unknown - moved to geographic location not served</option>
              </select>
            </div>
          </div>
        </div>

        {/* Emergent Care Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-red-600" />
            M2301. Emergent Care
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Has the patient used a hospital emergency department since SOC/ROC?</Label>
              <div className="space-y-2 mt-2">
                {[
                  { value: 0, label: '0 - No' },
                  { value: 1, label: '1 - Yes, WITHOUT hospital admission' },
                  { value: 2, label: '2 - Yes, WITH hospital admission' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.usedEmergencyDept === option.value}
                      onChange={() => handleInputChange('usedEmergencyDept', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {(formData.usedEmergencyDept === 1 || formData.usedEmergencyDept === 2) && (
              <div className="border-t pt-4">
                <Label className="text-sm">M2310. Reason for Emergent Care (check all that apply)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    'Medication issues',
                    'Hypo/Hyperglycemia',
                    'Falls/Injury',
                    'Respiratory issues',
                    'Cardiac issues',
                    'Infection',
                    'Pain management',
                    'Other'
                  ].map(reason => (
                    <label key={reason} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <Checkbox
                        checked={(formData.emergentCareReason || []).includes(reason)}
                        onCheckedChange={(checked) => handleArrayChange('emergentCareReason', reason, checked)}
                      />
                      <span className="text-sm">{reason}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <Label className="text-sm">M2410. Inpatient Facility Admission</Label>
              <div className="space-y-2 mt-2">
                {[
                  { value: null, label: 'NA - No inpatient facility admission' },
                  { value: 1, label: '1 - Hospital' },
                  { value: 2, label: '2 - Rehabilitation facility' },
                  { value: 3, label: '3 - Nursing home' },
                  { value: 4, label: '4 - Hospice' }
                ].map(option => (
                  <label key={option.value || 'na'} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={formData.inpatientFacility === option.value}
                      onChange={() => handleInputChange('inpatientFacility', option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Medication Reconciliation */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Pill className="w-5 h-5 text-purple-600" />
            Medication List Transfer
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm">A2121. Medication list provided to subsequent provider?</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.medListToProvider === true}
                    onChange={() => handleInputChange('medListToProvider', true)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.medListToProvider === false}
                    onChange={() => handleInputChange('medListToProvider', false)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-sm">A2123. Medication list provided to patient/caregiver?</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.medListToPatient === true}
                    onChange={() => handleInputChange('medListToPatient', true)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.medListToPatient === false}
                    onChange={() => handleInputChange('medListToPatient', false)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
            </div>

            {(formData.medListToProvider || formData.medListToPatient) && (
              <div className="border-t pt-4">
                <Label className="text-sm">Route of Transmission (check all that apply)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    'Electronic Health Record',
                    'Health Information Exchange',
                    'Verbal (phone, in-person)',
                    'Paper-based (fax, printout)',
                    'Other (email, CD)'
                  ].map(route => (
                    <label key={route} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <Checkbox
                        checked={(formData.medListTransmissionRoute || []).includes(route)}
                        onCheckedChange={(checked) => handleArrayChange('medListTransmissionRoute', route, checked)}
                      />
                      <span className="text-sm">{route}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Supportive Assistance Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-600" />
            Supportive Assistance Summary
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Primary Caregiver Name</Label>
              <Input
                value={formData.primaryCaregiverName || ''}
                onChange={(e) => handleInputChange('primaryCaregiverName', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Caregiver Phone</Label>
              <Input
                value={formData.primaryCaregiverPhone || ''}
                onChange={(e) => handleInputChange('primaryCaregiverPhone', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm">Discharge Plan Reviewed (check all that apply)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {[
                'Doctor appointments',
                'Follow-up appointments',
                'Disease management',
                'Medication safety',
                'Pain management',
                'Diet/Nutrition',
                'Wound care',
                'Diabetic foot care',
                'Emergency contacts',
                'Fall prevention',
                'Activity level',
                'Equipment/Supplies'
              ].map(item => (
                <label key={item} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Checkbox
                    checked={(formData.dischargePlanReviewed || []).includes(item)}
                    onCheckedChange={(checked) => handleArrayChange('dischargePlanReviewed', item, checked)}
                  />
                  <span className="text-sm">{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Functional Status at Discharge */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-600" />
            Functional Status at Discharge
          </h2>
          
          <div className="space-y-4">
            <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
              <p className="font-medium">Scoring Scale:</p>
              <p>06 = Independent | 05 = Setup | 04 = Supervision | 03 = Partial assist | 02 = Substantial assist | 01 = Dependent</p>
            </div>

            {[
              { field: 'selfCareScore', label: 'Self-Care Score (GG0130)' },
              { field: 'mobilityScore', label: 'Mobility Score (GG0170)' },
              { field: 'feedingScore', label: 'Feeding/Eating Score (M1870)' }
            ].map(({ field, label }) => (
              <div key={field}>
                <Label className="text-sm">{label}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[6, 5, 4, 3, 2, 1].map(value => (
                    <label key={value} className="flex items-center gap-1 p-2 bg-gray-50 rounded min-w-[60px]">
                      <input
                        type="radio"
                        checked={formData[field] === value}
                        onChange={() => handleInputChange(field, value)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">0{value}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Intervention Synopsis */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            M2401. Intervention Synopsis
          </h2>
          
          <p className="text-sm text-gray-600 mb-4">Were the following interventions included in the POC and implemented?</p>
          
          <div className="space-y-3">
            {[
              { field: 'fallPreventionIncluded', label: 'Falls prevention interventions' },
              { field: 'depressionIntervention', label: 'Depression intervention' },
              { field: 'painIntervention', label: 'Pain monitoring/mitigation' },
              { field: 'pressureUlcerPrevention', label: 'Pressure ulcer prevention' }
            ].map(({ field, label }) => (
              <div key={field} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="text-sm">{label}</span>
                <div className="flex gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={formData[field] === true}
                      onChange={() => handleInputChange(field, true)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Yes</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={formData[field] === false}
                      onChange={() => handleInputChange(field, false)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">No</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={formData[field] === null}
                      onChange={() => handleInputChange(field, null)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">N/A</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Discharge Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Discharge Summary
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Was this a planned discharge?</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.wasPlannedDischarge === true}
                    onChange={() => handleInputChange('wasPlannedDischarge', true)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.wasPlannedDischarge === false}
                    onChange={() => handleInputChange('wasPlannedDischarge', false)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Reason for Discharge</Label>
              <Input
                value={formData.reasonForDischarge || ''}
                onChange={(e) => handleInputChange('reasonForDischarge', e.target.value)}
                placeholder="e.g., Goals met, Patient request, Transferred to hospice"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Discharge Summary / Care Provided</Label>
              <textarea
                value={formData.dischargeSummary || ''}
                onChange={(e) => handleInputChange('dischargeSummary', e.target.value)}
                className="w-full mt-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12]"
                rows={5}
                placeholder="Describe skilled care provided, patient response, and discharge plan..."
              />
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Signatures
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Patient/Caregiver Signature (Type name)</Label>
              <Input
                value={formData.patientSignature || ''}
                onChange={(e) => handleInputChange('patientSignature', e.target.value)}
                placeholder="Type full name to sign"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Clinician Signature (Type name)</Label>
              <Input
                value={formData.clinicianSignature || ''}
                onChange={(e) => handleInputChange('clinicianSignature', e.target.value)}
                placeholder="Type full name to sign"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/clinical/oasis')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              variant="outline"
            >
              {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Draft</>}
            </Button>
            {formId && (
              <Button 
                onClick={handleComplete}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Complete Discharge
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DischargeAssessmentForm;
